import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Button, Group, TextInput, Select, Title, Pagination, Badge, ActionIcon, Card, Text, Image } from '@mantine/core'
import { IconPlus, IconSearch, IconEdit, IconTrash } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import ConfirmModal from '@/components/ConfirmModal'
import { api } from '@/lib/api'

export default function VideoList() {
  const navigate = useNavigate()
  const [videos, setVideos] = useState<Record<string, unknown>[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1 })
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string | null>('Бүгд')
  const [categories, setCategories] = useState<string[]>(['Бүгд'])
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(() => {
    const params: Record<string, string> = { page: String(pagination.page), limit: '20' }
    if (category && category !== 'Бүгд') params.category = category
    if (search) params.search = search
    api.admin.getVideos(params).then((res) => {
      setVideos(res.data)
      setPagination((p) => ({ ...p, pages: res.pagination.pages }))
    })
  }, [pagination.page, category, search])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    api.admin.getCategories().then((res) => {
      setCategories(['Бүгд', ...res.videoCategories])
    }).catch(() => {})
  }, [])

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteVideo(deleteId)
      notifications.show({ title: 'Амжилттай', message: 'Видео устгагдлаа', color: 'green' })
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Устгахад алдаа гарлаа', color: 'red' })
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={2}>Видео</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/videos/new')}>
          Шинэ видео
        </Button>
      </Group>

      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ flex: 1, minWidth: 180 }}
        />
        <Select
          data={categories}
          value={category}
          onChange={(v) => { setCategory(v); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ minWidth: 150 }}
        />
      </Group>

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Гарчиг</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Ангилал</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хугацаа</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үзсэн</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үнэ</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Онцлох</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>HLS</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {videos.map((v, i) => (
                <Table.Tr key={v._id as string}>
                  <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Image src={v.thumbnailUrl as string} w={56} h={36} radius="sm" fit="cover" fallbackSrc="https://placehold.co/56x36?text=..." />
                      <Text size="sm" fw={500} lineClamp={1}>{v.title as string}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>{v.category as string}</Table.Td>
                  <Table.Td c="dimmed">{v.duration as string}</Table.Td>
                  <Table.Td fw={500}>{(v.viewCount as number)?.toLocaleString()}</Table.Td>
                  <Table.Td fw={500}>{(v.price as number) ? `${(v.price as number).toLocaleString()}₮` : 'Үнэгүй'}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={v.isFeatured ? 'blue' : 'gray'}>{v.isFeatured ? 'Тийм' : 'Үгүй'}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {v.transcodingStatus === 'completed' && <Badge variant="light" color="green" size="sm">Бэлэн</Badge>}
                    {v.transcodingStatus === 'processing' && <Badge variant="light" color="blue" size="sm">Боловсруулж байна</Badge>}
                    {v.transcodingStatus === 'pending' && <Badge variant="light" color="yellow" size="sm">Хүлээгдэж буй</Badge>}
                    {v.transcodingStatus === 'failed' && <Badge variant="light" color="red" size="sm">Алдаа</Badge>}
                    {!v.transcodingStatus && <Badge variant="light" color="gray" size="sm">—</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <ActionIcon variant="light" color="blue" radius="md" onClick={() => navigate(`/videos/${v._id}/edit`)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(v._id as string)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {videos.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text ta="center" c="dimmed" py="xl">Видео олдсонгүй</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))} />
          </Group>
        )}
      </Card>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />
    </div>
  )
}
